import { FavoriteType } from '@kcftech/prometheus-client';
import { useQuery } from '@tanstack/react-query';
import { REFRESH_INTERVAL } from 'configs/constants';
import { sdClient } from 'contexts/SDKClientContext';
import { useFavoritesByType } from 'custom_hooks/data_hooks/favorites/useFavoritesByType';
import { CorporationOverviewFilters } from 'pages/Corporation/types';
import { Filter } from 'utils/query_v3';

interface UseCorporationIssuesOptions {
    corporationId: string;
    filters?: CorporationOverviewFilters;
    enabled?: boolean;
    refetchInterval?: number;
    staleTime?: number;
}

async function fetchCorporationIssuesSummary(
    corporationId: string,
    locationIds?: string[],
) {
    try {
        return await sdClient.getIssueSummaryForLocationsByCorporation({
            corporationId,
            filter: locationIds
                ? Filter.in('Location.LocationId', locationIds)
                : undefined,
        });
    } catch (error) {
        throw new Error(
            `Failed to fetch issues for corporation ${corporationId}: ${
                error instanceof Error ? error.message : 'Unknown error'
            }`,
        );
    }
}

/**
 * Hook to fetch issue summary data for all locations within a corporation
 */
export function useCorporationIssuesSummary({
    corporationId,
    filters,
    enabled = true,
    refetchInterval = REFRESH_INTERVAL,
    staleTime = REFRESH_INTERVAL,
}: UseCorporationIssuesOptions) {
    const favorites = useFavoritesByType({ type: FavoriteType.Location, corporationId });
    const locationIds =
        filters?.favoritesFilter === 'favorites'
            ? favorites.map((fav) => fav.FavoriteId)
            : undefined;

    const queryResult = useQuery({
        queryKey: ['corporation-issues', corporationId, locationIds],
        queryFn: () => fetchCorporationIssuesSummary(corporationId, locationIds),
        enabled: enabled && !!corporationId,
        refetchInterval,
        staleTime,
    });

    return queryResult;
}
